import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate, Interview } from '../types/hr.types';
import { ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon, User } from 'lucide-react';
import { getMonthDays, getToday } from '../utils/dateUtils';
import { mockInterviews } from '../utils/mockData';
interface ScheduleInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidate: Candidate | null;
  onSchedule: (date: Date) => void;
}
export function ScheduleInterviewModal({
  isOpen,
  onClose,
  candidate,
  onSchedule
}: ScheduleInterviewModalProps) {
  const [currentDate, setCurrentDate] = useState(getToday());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [calendarDays, setCalendarDays] = useState<(Date | null)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dayInterviews, setDayInterviews] = useState<Interview[]>([]);
  useEffect(() => {
    setCalendarDays(getMonthDays(currentDate.getFullYear(), currentDate.getMonth()));
  }, [currentDate]);
  useEffect(() => {
    if (selectedDate) {
      setDayInterviews(mockInterviews.filter(i => {
        const d = new Date(i.dateTime);
        return d.getDate() === selectedDate.getDate() && d.getMonth() === selectedDate.getMonth() && d.getFullYear() === selectedDate.getFullYear();
      }).sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()));
    } else {
      setDayInterviews([]);
    }
  }, [selectedDate]);
  useEffect(() => {
    if (isOpen) {
      const t = getToday();
      setCurrentDate(t);
      setSelectedDate(t);
      setSelectedTime('');
      setIsLoading(false);
    }
  }, [isOpen]);
  const handleDateClick = (date: Date) => {
    const t = getToday();
    t.setHours(0, 0, 0, 0);
    if (date < t) return;
    setSelectedDate(date);
    setSelectedTime('');
  };
  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) return;
    setIsLoading(true);
    const [h, m] = selectedTime.split(':').map(Number);
    const dt = new Date(selectedDate);
    dt.setHours(h, m, 0, 0);
    await new Promise(r => setTimeout(r, 800));
    onSchedule(dt);
    setIsLoading(false);
  };
  const timeSlots: string[] = [];
  for (let h = 9; h <= 17; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    if (h !== 17) timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
  }
  const monthName = currentDate.toLocaleString('default', {
    month: 'long',
    year: 'numeric'
  });
  const today = getToday();
  today.setHours(0, 0, 0, 0);
  if (!candidate) return null;
  return <Modal isOpen={isOpen} onClose={onClose} title={`Schedule Interview: ${candidate.firstName} ${candidate.lastName}`} maxWidth="2xl" data-id="element-1011">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full" data-id="element-1012">
        <div className="space-y-4" data-id="element-1013">
          <div className="flex items-center justify-between mb-2" data-id="element-1014">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-1015">
              <CalendarIcon className="h-4 w-4 text-gray-500" data-id="element-1016" /> Select Date
            </h3>
            <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1" data-id="element-1017">
              <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} className="p-1 hover:bg-white hover:shadow-sm rounded-md transition-all" data-id="element-1018">
                <ChevronLeft className="h-4 w-4 text-gray-600" data-id="element-1019" />
              </button>
              <span className="text-sm font-medium w-32 text-center text-gray-700" data-id="element-1020">
                {monthName}
              </span>
              <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} className="p-1 hover:bg-white hover:shadow-sm rounded-md transition-all" data-id="element-1021">
                <ChevronRight className="h-4 w-4 text-gray-600" data-id="element-1022" />
              </button>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-1023">
            <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-1024">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <div key={`${d}-${i}`} className="text-xs font-semibold text-gray-400 py-1" data-id="element-1025">
                  {d}
                </div>)}
            </div>
            <div className="grid grid-cols-7 gap-1" data-id="element-1026">
              {calendarDays.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} className="h-10" data-id="element-1027" />;
              const isSelected = selectedDate && date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
              const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
              const isPast = date < today;
              const hasInterviews = mockInterviews.some(i => {
                const d = new Date(i.dateTime);
                return d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
              });
              return <button key={idx} onClick={() => handleDateClick(date)} disabled={isPast} className={`h-10 flex flex-col items-center justify-center rounded-lg text-sm relative transition-all duration-200 ${isSelected ? 'bg-orange-600 text-white shadow-md scale-105 z-10' : isToday ? 'bg-orange-50 text-orange-700 font-bold border border-orange-200' : isPast ? 'text-gray-300 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50 text-gray-700 hover:border-gray-200 border border-transparent'}`} data-id="element-1028">
                    <span data-id="element-1029">{date.getDate()}</span>
                    {hasInterviews && !isSelected && !isPast && <div className="absolute bottom-1.5 h-1 w-1 rounded-full bg-orange-400" data-id="element-1030" />}
                    {hasInterviews && isSelected && <div className="absolute bottom-1.5 h-1 w-1 rounded-full bg-white/70" data-id="element-1031" />}
                  </button>;
            })}
            </div>
          </div>
        </div>
        <div className="flex flex-col h-full border-l border-gray-100 pl-0 md:pl-6" data-id="element-1032">
          <div className="flex-1 space-y-6" data-id="element-1033">
            <div data-id="element-1034">
              <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1035">
                {selectedDate ? selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              }) : 'Select a date'}
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1036">
                {dayInterviews.length} existing interview
                {dayInterviews.length !== 1 ? 's' : ''} scheduled
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 max-h-[200px] overflow-y-auto" data-id="element-1037">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1038">
                Schedule for this day
              </h4>
              {dayInterviews.length > 0 ? <div className="space-y-3" data-id="element-1039">
                  {dayInterviews.map(interview => {
                const time = new Date(interview.dateTime).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                });
                return <div key={interview.id} className="flex items-start gap-3 text-sm bg-white p-2.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1040">
                        <div className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap mt-0.5" data-id="element-1041">
                          {time}
                        </div>
                        <div data-id="element-1042">
                          <p className="font-medium text-gray-900" data-id="element-1043">
                            {interview.candidateName}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5" data-id="element-1044">
                            <User className="h-3 w-3" data-id="element-1045" />
                            <span data-id="element-1046">{interview.interviewer}</span>
                          </div>
                        </div>
                      </div>;
              })}
                </div> : <div className="text-center py-8 text-gray-400 text-sm" data-id="element-1047">
                  No interviews scheduled for this day
                </div>}
            </div>
            <div data-id="element-1048">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2" data-id="element-1049">
                <Clock className="h-4 w-4 text-gray-500" data-id="element-1050" /> Select Time
              </label>
              <div className="grid grid-cols-4 gap-2" data-id="element-1051">
                {timeSlots.map(time => <button key={time} onClick={() => setSelectedTime(time)} disabled={!selectedDate} className={`px-2 py-2 text-sm rounded-lg border transition-all duration-200 ${selectedTime === time ? 'bg-orange-600 text-white border-orange-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50'} ${!selectedDate ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1052">
                    {time}
                  </button>)}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-100" data-id="element-1053">
            <Button variant="ghost" onClick={onClose} disabled={isLoading} data-id="element-1054">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} isLoading={isLoading} disabled={!selectedDate || !selectedTime} leftIcon={<CalendarIcon className="h-4 w-4" data-id="element-1056" />} data-id="element-1055">
              Schedule Interview
            </Button>
          </div>
        </div>
      </div>
    </Modal>;
}